import { useEffect, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { toast } from 'react-toastify';

import FHAutocomplete from '../../../components/base/FHAutocomplete';
import FHButton from '../../../components/base/FHButton';
import FHConfirmDialog from '../../../components/base/dialogs/FHConfirmDialog';
import FHInputField from '../../../components/base/FHInputField';
import FHSelect from '../../../components/base/FHSelect';
import EditOutlinedIcon from '../../../components/svgs/EditOutlinedIcon';
import TrashIcon from '../../../components/svgs/TrashIcon';
import { Themes } from '../../../constants';
import { FORTA_BLUE } from '../../../utils/colors';
import { DELIVERY_METHOD_OPTIONS, LOCATION_OPTIONS } from '../../constants/modifierMatrix';
import { MODIFIER_REGEX } from './constants';
import {
  ACTIONS_CELL_SX,
  DELIVERY_METHOD_CELL_SX,
  LOCATION_CELL_SX,
  MODIFIER_CELL_SX,
  MODIFIER_INPUT_SX,
  MODIFIER_ROW_ICON_BUTTON_SX,
  MODIFIER_ROW_SX,
  PAYOR_CELL_SX,
  SERVICE_CODE_CELL_SX,
} from './styles';

import type { ChangeEvent, HTMLAttributes, SyntheticEvent } from 'react';
import type { IServiceCode, SelectValueType } from '../../../utils/types';
import type { ClientModifierMatrixRowProps, ModifierField } from './types';

const ClientModifierMatrixRow = ({
  row,
  serviceCodeOptions,
  payorOptions,
  isAdding,
  isEditing,
  isDisabled,
  onEdit,
  onCancel,
  onSave,
  onDelete,
  onValidationError = toast.error,
}: ClientModifierMatrixRowProps) => {
  const [editableRow, setEditableRow] = useState({ ...row });
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Re-sync the edit draft to the latest row prop whenever this row is not actively being
  // edited/added, so a stale unsaved draft never reappears after switching rows mid-edit.
  useEffect(() => {
    if (!isAdding && !isEditing) {
      setEditableRow({ ...row });
    }
  }, [isAdding, isEditing, row]);

  const handleServiceCodeChange = (_event: SyntheticEvent, value: IServiceCode | null) =>
    setEditableRow((prevRow) => ({ ...prevRow, service_code_id: value?.id ?? null }));

  const handleLocationChange = (value: SelectValueType) =>
    setEditableRow((prevRow) => ({ ...prevRow, location: String(value) }));

  const handleDeliveryMethodChange = (value: SelectValueType) =>
    setEditableRow((prevRow) => ({ ...prevRow, delivery_method: String(value) }));

  const handlePayorChange = (value: SelectValueType) =>
    setEditableRow((prevRow) => ({ ...prevRow, payor_id: Number(value) }));

  const handleModifierChange = (field: ModifierField, value: string) =>
    setEditableRow((prevRow) => ({ ...prevRow, [field]: value }));

  const validate = () => {
    if (!editableRow.payor_id || !editableRow.service_code_id || !editableRow.location || !editableRow.delivery_method) {
      onValidationError('Payor, service code, location, and delivery method are required');
      return false;
    }
    const modifiers = [
      editableRow.modifier_1,
      editableRow.modifier_2,
      editableRow.modifier_3,
      editableRow.modifier_4,
    ];
    for (const modifier of modifiers) {
      // Whitespace-only inputs normalize to null on save, so only validate the trimmed value.
      const trimmedModifier = modifier?.trim();
      if (trimmedModifier && !MODIFIER_REGEX.test(trimmedModifier)) {
        onValidationError('Each modifier must be 2 alphanumeric characters');
        return false;
      }
    }
    return true;
  };

  const handleSave = () => {
    if (validate()) {
      onSave(editableRow);
    }
  };

  const handleCancel = () => {
    setEditableRow({ ...row });
    onCancel();
  };

  // Close the dialog before dispatching the delete so a second click can't fire a duplicate
  // DELETE (the first succeeds, the second 404s and surfaces a misleading error toast).
  const handleConfirmDelete = () => {
    setShowConfirmDialog(false);
    onDelete(editableRow);
  };

  const renderModifier = (field: ModifierField) => (
    <Box sx={MODIFIER_CELL_SX}>
      {isAdding || isEditing ? (
        <FHInputField
          inputProps={{ 'data-testid': field, maxLength: 2 }}
          fullWidth
          inputHeight={40}
          sx={MODIFIER_INPUT_SX}
          value={editableRow[field] ?? ''}
          onChange={(event: ChangeEvent<HTMLInputElement>) =>
            handleModifierChange(field, event.target.value.toUpperCase())
          }
        />
      ) : (
        <Typography textAlign="center">{row[field]}</Typography>
      )}
    </Box>
  );

  return (
    <Box sx={MODIFIER_ROW_SX}>
      <Box sx={PAYOR_CELL_SX}>
        {isAdding || isEditing ? (
          <FHSelect
            data-testid="modifierPayor"
            inputHeight={40}
            sx={{ width: '100%', boxShadow: 'none' }}
            options={payorOptions}
            selectFirstOption={false}
            value={editableRow.payor_id ?? ''}
            onUpdate={handlePayorChange}
          />
        ) : (
          <Typography textAlign="left">
            {payorOptions.find((option) => option.value === row.payor_id)?.label}
          </Typography>
        )}
      </Box>
      <Box sx={SERVICE_CODE_CELL_SX}>
        {isAdding || isEditing ? (
          <FHAutocomplete
            data-testid="modifierServiceCodeDropdownId"
            fullWidth
            sx={{ width: '100%', minWidth: 0, boxShadow: 'none' }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            placeholder="Select service code"
            clearIcon={false}
            value={
              serviceCodeOptions.find((option) => option.id === editableRow.service_code_id) || null
            }
            isOptionEqualToValue={(option: IServiceCode, value: IServiceCode) =>
              option.id === value.id
            }
              renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IServiceCode) => (
              <li {...props} key={option.id} data-testid={`modifierServiceCode${option.code}`}>
                {option.label}
              </li>
            )}
            options={serviceCodeOptions}
            onChange={handleServiceCodeChange}
          />
        ) : (
          <Typography textAlign="left">
            {serviceCodeOptions.find((option) => option.id === row.service_code_id)?.label ??
              row.service_code}
          </Typography>
        )}
      </Box>
      <Box sx={LOCATION_CELL_SX}>
        {isAdding || isEditing ? (
          <FHSelect
            data-testid="modifierLocation"
            inputHeight={40}
            sx={{ width: '100%', boxShadow: 'none' }}
            options={LOCATION_OPTIONS}
            selectFirstOption={false}
            value={editableRow.location ?? ''}
            onUpdate={handleLocationChange}
          />
        ) : (
          <Typography textAlign="center">{row.location}</Typography>
        )}
      </Box>
      <Box sx={DELIVERY_METHOD_CELL_SX}>
        {isAdding || isEditing ? (
          <FHSelect
            data-testid="modifierDeliveryMethod"
            inputHeight={40}
            sx={{ width: '100%', boxShadow: 'none' }}
            options={DELIVERY_METHOD_OPTIONS}
            selectFirstOption={false}
            value={editableRow.delivery_method ?? ''}
            onUpdate={handleDeliveryMethodChange}
          />
        ) : (
          <Typography textAlign="center">{row.delivery_method}</Typography>
        )}
      </Box>
      {renderModifier('modifier_1')}
      {renderModifier('modifier_2')}
      {renderModifier('modifier_3')}
      {renderModifier('modifier_4')}
      <Box sx={ACTIONS_CELL_SX}>
        {isAdding || isEditing ? (
          <>
            <FHButton data-testid="saveModifierButton" disabled={isDisabled} onClick={handleSave}>
              Save
            </FHButton>
            <FHButton data-testid="cancelModifierButton" onClick={handleCancel}>
              Cancel
            </FHButton>
          </>
        ) : (
          <>
            <IconButton
              data-testid="editModifierButton"
              sx={MODIFIER_ROW_ICON_BUTTON_SX}
              disabled={isDisabled}
              onClick={onEdit}
            >
              <EditOutlinedIcon color={FORTA_BLUE.primary} width={18} />
            </IconButton>
            <IconButton
              data-testid="deleteModifierButton"
              sx={MODIFIER_ROW_ICON_BUTTON_SX}
              disabled={isDisabled}
              onClick={() => setShowConfirmDialog(true)}
            >
              <TrashIcon color={FORTA_BLUE.primary} width={20} />
            </IconButton>
          </>
        )}
      </Box>
      <FHConfirmDialog
        data-testid="modifierConfirmDialog"
        open={showConfirmDialog}
        colorTheme={Themes.Primary}
        title={'Delete Modifier Row'}
        content={'Are you sure you want to delete this modifier row? This action cannot be undone.'}
        submittext={'Yes, Delete'}
        canceltext={'Cancel'}
        onClose={() => setShowConfirmDialog(false)}
        onSubmit={handleConfirmDelete}
      />
    </Box>
  );
};

export default ClientModifierMatrixRow;
